import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  login,
  register,
  UserSession,
  sanitize,
  kebabToCamel,
  kebabToTitle,
  toTitleCase
} from '../utils'
import { createMember } from '../appwrite/methods'
import logo from "../assets/tom-logo-icon-black.jpg"
import { ID, Client, Account, Databases, Functions } from "appwrite";
import { AppProvider } from '@toolpad/core/AppProvider';
import { SignInPage } from '@toolpad/core/SignInPage';
import { useTheme, createTheme } from '@mui/material/styles';
import { Resend } from 'resend';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Link,
  IconButton,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';


const client = new Client()
  .setEndpoint(process.env.REACT_APP_ENDPOINT)
  .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);
const databases = new Databases(client);
const functions = new Functions(client);


const providers = [{ id: 'credentials', name: 'Email and Password' }];

function LogoButton() {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={() => navigate('/')}
      sx={{
        position: 'relative',
        display: 'flex',
        mt: '-5%',
        mb: '5%',
        height: '2.5em',
        width: 'fit-content',
        alignItems: 'center'
      }}
    >
      <ArrowBackIosNewOutlinedIcon sx={{ mr: '0.5em', fontSize: '1em' }} />
      Home&nbsp;
    </Button>
  );
}

const BRANDING = {
  logo: (<>
    <LogoButton />
    <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
      <img
        src={logo}
        alt="Takeoff Monkey Widgets Logo"
        style={{ height: 48 }}
      />
      <Typography variant="h5" sx={{ cursor: 'default', userSelect: 'none' }}>+</Typography>
      <img
        src={logo}
        alt="Takeoff Monkey Widgets Logo"
        style={{ height: 48 }}
      />
    </Stack>
  </>),
  title: 'Takeoff Monkey Widgets',
};


export default function Welcome() {
  const navigate = useNavigate();
  const [session, setSession] = useAtom(UserSession);
  const [inviteData, setInviteData] = useState(false);
  const [loading, setLoading] = useState(true);

  const urlParams = new URLSearchParams(window.location.search);
  const team = urlParams.get('team');
  const inviteKey = urlParams.get('key');


  useEffect(() => {
    if (!team || !inviteKey) {
      navigate('/');
      return;
    }

    const getSession = async () => {
      try {
        const user = await account.get()
        if (user) setSession(user);
      } catch (error) {
        setSession(null);
      }

      // Get active invites to determine if this user can join team
      try {
        await functions.createExecution(
          "db-get-document",
          JSON.stringify({
            'database_id': team,
            'collection_id': 'invites',
            'document_id': inviteKey
          }),
          false,
          '/',
          'GET'
        ).then((data) => {
          const getInviteData = JSON.parse(data.responseBody).response
          if (getInviteData) setInviteData(getInviteData);
          setLoading(false);
        })
      } catch (error) {
        setInviteData(false);
      }
    }
    getSession();
  }, []);
  
  function CompanyTitle() {
    return (<>
      {inviteData?.$databaseId && session && session?.labels?.includes(kebabToCamel(inviteData?.$databaseId)) && session?.email === inviteData?.email ?
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            You are already a member of this team.
          </Alert> :
        inviteData && <Typography variant="h6" sx={{ mt: 1.5, mb: 2, color: 'text.secondary', textAlign: 'center' }}>
          Joining as
          {inviteData?.roles?.map((role) =>
            <Chip
              key={role}
              label={toTitleCase(role)}
              sx={{ ml: 0.5, mb: 0.25, fontSize: '0.75em' }}
              variant={true ? "filled" : "outlined"}
              size="medium"
            />
          )}
        </Typography>
      }
      {!session && <br/> &&
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}>
        Enter a username and password to create a new Takeoff Monkey Widgets account
      </Typography>}
    </>)
  }

  function JoinButton() {
    return (
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!inviteData || (session && session.email !== inviteData.email)}
        sx={{ mt: 2 }}
      >
        {inviteData?.$databaseId && session?.labels?.includes(kebabToCamel(inviteData?.$databaseId)) ? "Login to Team" : "Join Team"}
      </Button>
    );
  }

  const handleSignIn = async (provider, formData, callbackUrl) => {
    const userID = sanitize(formData.get('email')) || session?.$id;
    const username = formData.get('email') || session?.name;
    const password = formData.get('password');

    try {
      // Create a new account if this user doesn't already have one
      if (!session) {
        // createMember(inviteData?.$databaseId, userID, username, ['editor'])
      }

      // Join the team
      if (session?.labels.includes(kebabToCamel(inviteData?.$databaseId))) {
        console.warn("User is already a member of this team; redirecting to team homepage and deleting invite.")
        await databases.deleteDocument(
          inviteData?.$databaseId,
          'invites',
          inviteKey
        ).then(() => {
          navigate(`/?team=${inviteData?.$databaseId}`)
        })
        return
      }
    } catch (error) {
      console.error(error);
      alert("Error joining team: " + error.message);
    }
  }

  const theme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  return (
    <AppProvider branding={BRANDING} theme={theme}>
      <Stack direction="row" sx={{ maxWidth: '500px', mx: 'auto' }}>
        <SignInPage
          signIn={handleSignIn}
          providers={providers}
          slotProps={{
            emailField: {
              label: 'Username',
              name: 'username',
              type: 'text',
              autoFocus: false,
              autoComplete: 'username',
              required: true,
              placeholder: 'John Smith',
            },
            passwordField: {
              label: 'Password',
              name: 'password',
              type: 'password',
              required: true,
            },
            form: {
              noValidate: true,
              sx: { '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' } }
            },
            submitButton: {
              sx: { mt: 2 }
            }
          }}
          slots={{
            title: () => <Typography
              variant="h4"
              sx={{ mt: 1, mb: 1, textAlign: 'center' }}
            >
              Welcome to<br/>{kebabToTitle(team || "")}
            </Typography>,
            subtitle: CompanyTitle,
            ...(loading ? { emailField: () => <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                overflow: 'hidden'
              }}>
                <CircularProgress />
              </Box> } : !inviteData || (session && session?.email !== inviteData.email) ? { emailField: () =>
                <Alert severity="error" variant="outlined">
                  This team invite is expired or invalid.
                </Alert> } :
              session ? { emailField: () => null } : {}
            ),
            ...(session || !inviteData ? { passwordField: () => null } : {}),
            submitButton: JoinButton,
            rememberMe: () => null
          }}
          sx={{ mx: 'auto' }}
        />
      </Stack>
    </AppProvider>
  );
}